import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Avatar,
  Box,
  Button,
  Grid,
  Paper,
  Typography,
  Container,
  Stack,
  IconButton,
  Tooltip,
  TextField,
} from "@mui/material";
import PerfectScrollbar from "react-perfect-scrollbar";
import "react-perfect-scrollbar/dist/css/styles.css";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import glass from "../../../image/pngwing.com (7) 2.png";
import Bottles from "../../../image/pngwing.com (13).png";
import * as XLSX from "xlsx";
import { useForm, Controller } from "react-hook-form";
import { reportStyles } from "../../components/reportStyles";
import { useDispatch, useSelector } from "react-redux";
import Pagination from "../../constant/CommonPagenation";
import { getColumnWidth } from "../../../style/datagridMQ";
import CustomDataGrid from "../../constant/commondatagrid";
import {
  wholesalerReportByIdDateSearchRequest,
  wholesalerReportByIdDateSearchReset,
  wholesalerReportByIdRequest,
  wholesalerReportMonthByIdDateSearchRequest,
  wholesalerReportMonthByIdDateSearchReset,
  wholesalerReportMonthByIdRequest,
  wholesalerReportWeekByIdDateSearchRequest,
  wholesalerReportWeekByIdDateSearchReset,
  wholesalerReportWeekByIdRequest,
} from "../../../redux/wholesalerReportById/WholesalerReportByIdAction";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import dayjs from "dayjs";
import { parse, format, isValid } from "date-fns";

import weekOfYear from "dayjs/plugin/weekOfYear";

dayjs.extend(weekOfYear);

const TIME_FRAMES = {
  MONTH: "Month",
  WEEK: "Week",
  DAY: "Day",
};

const formatCustomDate = (value, type) => {
  if (!value) return "";

  try {
    switch (type) {
      // case "day": {
      //   const date = parse(value, "yyyy-MM-dd", new Date());
      //   return isValid(date) ? format(date, "dd-MM-yyyy") : value;
      // }
      case "day": {
        const date = parse(value, "yyyy-MM-dd", new Date());
        return isValid(date) ? format(date, "dd MMM yyyy") : value;
      }

      case "month": {
        if (/^\d{4}-\d{2}$/.test(value)) {
          const date = parse(value, "yyyy-MM", new Date());
          return isValid(date) ? format(date, "MMMM yyyy") : value;
        }
        if (/^\d{6}$/.test(value)) {
          const year = value.slice(0, 4);
          const month = value.slice(4);
          return `${format(new Date(year, month - 1), "MMMM")} ${year}`;
        }
        return value;
      }

      case "week": {
        const match = value.match(/^(\d{4})-W(\d{1,2})$/);
        if (match) {
          return `Week ${match[2]}, ${match[1]}`;
        }
        if (/^\d{6}$/.test(value)) {
          return getFormattedWeek(value);
        }
        return value;
      }
      default:
        return value;
    }
  } catch {
    return value;
  }
};

const getFormattedWeek = (weekStr) => {
  if (!weekStr || weekStr.length !== 6) return "N/A";

  const year = weekStr.slice(0, 4);
  const week = weekStr.slice(4);

  return `Week ${week}, ${year}`;
};

const formatDecimal = (value, decimalPlaces = 2) => {
  if (typeof value === "number") {
    return value.toFixed(decimalPlaces);
  }
  if (!isNaN(parseFloat(value))) {
    return parseFloat(value).toFixed(decimalPlaces);
  }
  return value;
};

const COLUMN_DEFINITIONS = {
  day: [
    {
      field: "Transactions",
      headerName: "Amount",
      width: getColumnWidth("column4"),
      renderCell: (params) => formatDecimal(params.value),
    },
    {
      field: "BottlesSold",
      headerName: "Bottles Sold",
      width: getColumnWidth("column4"),
    },
    {
      field: "MostSoldBrand",
      headerName: "Most Sold Brand",
      width: getColumnWidth("column4"),
    },
    {
      field: "Date",
      headerName: "Date",
      width: getColumnWidth("column4"),
      renderCell: (params) => formatCustomDate(params.value, "day"),
    },
  ],
  week: [
    {
      field: "week",
      headerName: "Week",
      width: getColumnWidth("column4"),
      renderCell: (params) => formatCustomDate(params.value, "week"),
    },
    {
      field: "weeklyTransactions",
      headerName: "Amount",
      width: getColumnWidth("column4"),
      renderCell: (params) => formatDecimal(params.value),
    },
    {
      field: "BottlesSold",
      headerName: "Bottles Sold",
      width: getColumnWidth("column4"),
    },
    {
      field: "MostSoldBrand",
      headerName: "Most Sold Brand",
      width: getColumnWidth("column4"),
    },
  ],
  month: [
    {
      field: "month",
      headerName: "Month",
      width: getColumnWidth("column4"),
      renderCell: (params) => formatCustomDate(params.value, "month"),
    },

    {
      field: "monthlyTransactions",
      headerName: "Amount",
      width: getColumnWidth("column4"),
      renderCell: (params) => formatDecimal(params.value),
    },
    {
      field: "BottlesSold",
      headerName: "Bottles Sold",
      width: getColumnWidth("column4"),
    },
    {
      field: "MostSoldBrand",
      headerName: "Most Sold Brand",
      width: getColumnWidth("column4"),
    },
  ],
};

const WholesalerSalesReportForm = () => {
  const {
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
  });

  const startDate = watch("startDate", "");
  const endDate = watch("endDate", "");

  const [currentPage, setCurrentPage] = useState(1);
  const [timeFrame, setTimeFrame] = useState(TIME_FRAMES.MONTH);
  const [isDateSearchActive, setIsDateSearchActive] = useState(false);
  const [showChart, setShowChart] = useState(true);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);

  const dispatch = useDispatch();

  const id = useSelector(
    (state) => state?.values?.loginValues?.[0]?.wholesaler_id
  );

  const regularDayData = useSelector(
    (state) => state?.WholesalerReportById?.viewAllWholesalerReportsById || {}
  );
  const regularMonthData = useSelector(
    (state) => state?.WholesalerReportById?.monthWholesalerReportsById || {}
  );
  const regularWeekData = useSelector(
    (state) => state?.WholesalerReportById?.WeekWholesalerReportsById || {}
  );

  const searchDayData = useSelector(
    (state) =>
      state?.WholesalerReportById?.dateSearchWholesalerReportsById || {}
  );
  const searchMonthData = useSelector(
    (state) =>
      state?.WholesalerReportById?.monthDateSearchWholesalerReportsById || {}
  );
  const searchWeekData = useSelector(
    (state) =>
      state?.WholesalerReportById?.weekDateSearchWholesalerReportsById || {}
  );

  const totalRecordsDay = regularDayData.data?.totalRecords || 0;
  const totalRecordsWeek = regularWeekData.data?.totalRecords || 0;
  const totalRecordsMonth = regularMonthData.data?.totalRecords || 0;

  const getDataForTimeFrame = useCallback(
    (timeFrameKey, isSearch = false) => {
      const dataMap = {
        day: isSearch ? searchDayData : regularDayData,
        week: isSearch ? searchWeekData : regularWeekData,
        month: isSearch ? searchMonthData : regularMonthData,
      };

      const source = dataMap[timeFrameKey.toLowerCase()];
      const data = source?.data;

      if (!data) return { list: [], totalRecords: 0, pageSize: 10 };

      switch (timeFrameKey.toLowerCase()) {
        case "day":
          return {
            list: !isSearch
              ? data?.data?.dayWiseReport || data?.dayWiseReport || []
              : source?.totals?.allWholesalersDayWiseReport,
            totalTransactionsMonth: data?.data?.totalTransactionsMonth || [],
            totalTransactionsWeek: data?.data?.totalTransactionsWeek || [],
            totalRecords: source?.totalRecords || 0,
            pageSize: source?.pagesize || source?.pageSize || 10,
          };
        case "week":
          return {
            list: data?.data?.weekWiseReport || data?.weekWiseReport || [],
            totalRecords: source?.totalRecords || 0,
            pageSize: source?.pagesize || source?.pageSize || 10,
          };
        case "month":
          return {
            list: data?.data?.monthWiseReport || data?.monthWiseReport || [],
            totalRecords: source?.totalRecords || 0,
            pageSize: source?.pagesize || source?.pageSize || 10,
          };
        default:
          return { list: [], totalRecords: 0, pageSize: 10 };
      }
    },
    [
      regularDayData,
      regularMonthData,
      regularWeekData,
      searchDayData,
      searchMonthData,
      searchWeekData,
    ]
  );
  const currentData = useMemo(() => {
    return getDataForTimeFrame(timeFrame, isDateSearchActive);
  }, [getDataForTimeFrame, timeFrame, isDateSearchActive]);

  const regularData = useMemo(
    () => ({
      day: getDataForTimeFrame("DAY", false),
      week: getDataForTimeFrame("WEEK", false),
      month: getDataForTimeFrame("MONTH", false),
    }),
    [getDataForTimeFrame]
  );

  const transformedRows = useMemo(() => {
    const preprocessDailyData = (data) => {
      if (!Array.isArray(data)) return [];
      return data.map((item, index) => ({
        id: index + 1,
        Transactions: item?.transactions || "-",
        BottlesSold: Number(item?.totalBottlesSold) || "-",
        MostSoldBrand: item?.mostOrderedBrand || "N/A",
        Date: item?.date || "N/A",
      }));
    };

    const data = currentData.list;

    switch (timeFrame) {
      case TIME_FRAMES.DAY:
        return preprocessDailyData(data);

      case TIME_FRAMES.WEEK:
        return data.map((item, index) => ({
          id: index + 1,
          week: item?.week || "N/A",
          weeklyTransactions: item?.weeklyTransactions || "-",
          BottlesSold: Number(item?.totalBottlesSold) || "-",
          MostSoldBrand: item?.mostOrderedBrand || "N/A",
        }));

      case TIME_FRAMES.MONTH:
        return data.map((item, index) => ({
          id: index + 1,
          month: item?.month || "N/A",
          monthlyTransactions: item?.monthlyTransactions || "-",
          BottlesSold: Number(item?.totalBottlesSold) || "-",
          MostSoldBrand: item?.mostOrderedBrand || "N/A",
        }));

      default:
        return [];
    }
  }, [currentData?.list, timeFrame]);

  // Memoized columns
  const columns = useMemo(() => {
    switch (timeFrame) {
      case TIME_FRAMES.DAY:
        return COLUMN_DEFINITIONS.day;
      case TIME_FRAMES.WEEK:
        return COLUMN_DEFINITIONS.week;
      case TIME_FRAMES.MONTH:
        return COLUMN_DEFINITIONS.month;
      default:
        return COLUMN_DEFINITIONS.month;
    }
  }, [timeFrame]);

  // Memoized sales metrics
  const salesMetrics = useMemo(() => {
    let totalSales = 0;
    let avgSales = 0;

    switch (timeFrame) {
      case TIME_FRAMES.DAY: {
        const today = dayjs().format("YYYY-MM-DD");
        const todayData = regularData.day.list.find(
          (item) => item.date === today
        );
        if (todayData) {
          totalSales = todayData.transactions || 0;
          avgSales = totalSales;
        }
        break;
      }
      case TIME_FRAMES.WEEK: {
        const currentWeek = dayjs().format("YYYYWW");
        const weekData = regularData.week.list.find(
          (item) => item.week?.toString() === currentWeek
        );
        if (weekData) {
          totalSales = weekData.weeklyTransactions || 0;
        } else if (regularData.week.list.length > 0) {
          totalSales =
            regularData.week.list[regularData.week.list.length - 1]
              .weeklyTransactions || 0;
        }
        avgSales = totalSales / 7;
        break;
      }
      case TIME_FRAMES.MONTH: {
        const currentMonth = dayjs().format("YYYY-MM");
        const monthData = regularData.month.list.find(
          (item) => dayjs(item.month).format("YYYY-MM") === currentMonth
        );
        if (monthData) {
          totalSales = monthData.monthlyTransactions || 0;
        } else if (regularData.month.list.length > 0) {
          totalSales =
            regularData.month.list[regularData.month.list.length - 1]
              .monthlyTransactions || 0;
        }
        const daysInMonth = dayjs().daysInMonth();
        avgSales = totalSales / daysInMonth;
        break;
      }
    }

    return { totalSales, avgSales };
  }, [regularData, timeFrame]);

  const chartData = useMemo(() => {
    if (!showChart || isDateSearchActive) {
      return [];
    }

    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    switch (timeFrame) {
      case TIME_FRAMES.DAY: {
        const today = dayjs().startOf("day");
        const minDate = today.subtract(4, "day");

        const dayList = regularData?.day?.list;
        if (!Array.isArray(dayList)) {
          return [{ date: today.format("YYYY-MM-DD"), Transactions: 0 }];
        }

        const filteredData = dayList
          .filter((item) => {
            if (!item?.date) return false;
            const itemDate = dayjs(item.date).startOf("day");
            return itemDate.isSame(today, "day") || itemDate.isAfter(minDate);
          })
          .map((item) => ({
            date: item.date,
            Transactions: item.transactions || 0,
          }));

        return filteredData.length === 0
          ? [{ date: today.format("YYYY-MM-DD"), Transactions: 0 }]
          : filteredData;
      }

      case TIME_FRAMES.WEEK: {
        const weekList = regularData?.week?.list;
        if (!Array.isArray(weekList)) {
          const currentWeek = dayjs().week();
          const weeksArray = [];
          for (let w = Math.max(1, currentWeek - 11); w <= currentWeek; w++) {
            weeksArray.push({
              date: `Week ${w}`,
              Transactions: 0,
            });
          }
          return weeksArray;
        }

        const allWeeks = (() => {
          const availableWeeks = weekList
            .map((item) => Number(item?.week))
            .filter((week) => !isNaN(week) && week > 0);
          const maxWeek =
            availableWeeks.length > 0
              ? Math.max(...availableWeeks)
              : dayjs().week();
          const startWeek = Math.max(1, maxWeek - 11);

          const weeksArray = [];
          for (let w = startWeek; w <= maxWeek; w++) {
            weeksArray.push(w);
          }
          return weeksArray;
        })();

        return allWeeks.map((week) => {
          const weekData = weekList.find((item) => Number(item?.week) === week);
          return {
            date: `Week ${week}`,
            Transactions: weekData?.weeklyTransactions || 0,
          };
        });
      }

      case TIME_FRAMES.MONTH: {
        const monthList = regularData?.month?.list;
        if (!Array.isArray(monthList)) {
          return monthNames.map((month) => ({
            date: month,
            Transactions: 0,
          }));
        }

        return monthNames.map((month, index) => {
          const monthData = monthList.find(
            (item) => item?.month && dayjs(item.month).month() === index
          );
          return {
            date: month,
            Transactions: monthData?.monthlyTransactions || 0,
          };
        });
      }

      default:
        return [];
    }
  }, [regularData, timeFrame, showChart, isDateSearchActive]);

  // API call function
  const makeApiCall = useCallback(
    (isSubmitAction = false, targetTimeFrame = null) => {
      if (!id) return;

      const currentTimeFrame = targetTimeFrame || timeFrame;
      const hasDateRange = startDate && endDate;
      const apiParams = {
        wholesalerId: id,
        pageNo: currentPage,
        ...(hasDateRange && { start_date: startDate, end_date: endDate }),
      };

      const apiMap = {
        [TIME_FRAMES.DAY]: hasDateRange
          ? wholesalerReportByIdDateSearchRequest
          : wholesalerReportByIdRequest,
        [TIME_FRAMES.WEEK]: hasDateRange
          ? wholesalerReportWeekByIdDateSearchRequest
          : wholesalerReportWeekByIdRequest,
        [TIME_FRAMES.MONTH]: hasDateRange
          ? wholesalerReportMonthByIdDateSearchRequest
          : wholesalerReportMonthByIdRequest,
      };

      const actionCreator = apiMap[currentTimeFrame];

      if (actionCreator) {
        if (hasDateRange) {
          dispatch(actionCreator(apiParams));
        } else {
          dispatch(actionCreator(id, currentPage));
        }
      }
    },
    [dispatch, id, currentPage, timeFrame, startDate, endDate]
  );

  useEffect(() => {
    if (id && !hasInitialLoad) {
      makeApiCall();
      setHasInitialLoad(true);
    }
  }, [id, hasInitialLoad, makeApiCall]);

  useEffect(() => {
    if (id && isDateSearchActive && startDate && endDate) {
      makeApiCall(true);
    }
  }, [currentPage, id, isDateSearchActive, startDate, endDate, makeApiCall]);

  const handleSubmitDates = useCallback(() => {
    if (!startDate || !endDate) {
      alert("Please select both start and end dates");
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (start > end) {
      alert("Start date must be before end date");
      return;
    }

    setCurrentPage(1);
    setIsDateSearchActive(true);
    setShowChart(false);
    makeApiCall(true);
  }, [startDate, endDate, makeApiCall]);

  const handleReset = useCallback(() => {
    reset({ startDate: "", endDate: "" });
    setIsDateSearchActive(false);
    setShowChart(true);
    setCurrentPage(1);
    dispatch(wholesalerReportMonthByIdDateSearchReset());
    dispatch(wholesalerReportWeekByIdDateSearchReset());
    dispatch(wholesalerReportByIdDateSearchReset());
    // Load default data after reset
    makeApiCall();
  }, [reset, makeApiCall, dispatch]);

  const handleTimeFrameChange = useCallback(
    (newTimeFrame) => {
      setTimeFrame(newTimeFrame);
      setCurrentPage(1);
      setIsDateSearchActive(false);
      setShowChart(true);
      reset({ startDate: "", endDate: "" });
      makeApiCall(false, newTimeFrame);
    },
    [reset, makeApiCall]
  );

const handleExcelDownload = useCallback(() => {
  const getHeaderMappings = (timeFrame) => {
    const baseMapping = {
      id: "S.No",
    };

    switch (timeFrame) {
      case TIME_FRAMES.DAY:
        return {
          ...baseMapping,
          Transactions: "Amount",
          BottlesSold: "Bottles Sold",
          MostSoldBrand: "Most Sold Brand",
          Date: "Date",
        };
      case TIME_FRAMES.WEEK:
        return {
          ...baseMapping,
          week: "Week",
          weeklyTransactions: "Amount",
          BottlesSold: "Bottles Sold",
          MostSoldBrand: "Most Sold Brand",
        };
      case TIME_FRAMES.MONTH:
        return {
          ...baseMapping,
          month: "Month",
          monthlyTransactions: "Amount",
          BottlesSold: "Bottles Sold",
          MostSoldBrand: "Most Sold Brand",
        };
      default:
        return baseMapping;
    }
  };

  const formatNumberWithDecimals = (value, decimalPlaces = 2) => {
    if (value === null || value === undefined || value === "" || value === "-") return "0.00";
    const num = parseFloat(value);
    return isNaN(num) ? "0.00" : num.toFixed(decimalPlaces);
  };

  const shouldFormatAsDecimal = (key) => {
    const decimalFields = [
      "Transactions",
      "weeklyTransactions", 
      "monthlyTransactions",
      "Amount"
    ];
    return decimalFields.includes(key);
  };

  const dataForExport = transformedRows.map((row, index) => {
    const newRow = {};
    
    Object.keys(row).forEach((key) => {
      let value = row[key];

      if (key === "Date" && value && value !== "N/A") {
        value = formatCustomDate(value, "day");
      } else if (key === "week" && value && value !== "N/A") {
        value = formatCustomDate(value, "week");
      } else if (key === "month" && value && value !== "N/A") {
        value = formatCustomDate(value, "month");
      }

      if (shouldFormatAsDecimal(key)) {
        value = formatNumberWithDecimals(value);
      }

      const headerMappings = getHeaderMappings(timeFrame);
      const headerName = headerMappings[key] || key;
      
      newRow[headerName] = value;
    });

    newRow["S.No"] = index + 1;
    
    return newRow;
  });

  const worksheet = XLSX.utils.json_to_sheet(dataForExport);
  const workbook = XLSX.utils.book_new();
  
  const sheetName = `Wholesaler Sales Report - ${timeFrame}`;
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  const timestamp = new Date().toISOString().split("T")[0];
  const fileName = `Wholesaler_Sales_Report_${timeFrame}_${timestamp}.xlsx`;
  
  XLSX.writeFile(workbook, fileName);
}, [transformedRows, timeFrame]);

  // Loading state
  const isLoading = useMemo(() => {
    const loadingStates = [
      regularDayData.inProgress,
      regularMonthData.inProgress,
      regularWeekData.inProgress,
      searchDayData.inProgress,
      searchMonthData.inProgress,
      searchWeekData.inProgress,
    ];

    return loadingStates.some(Boolean);
  }, [
    regularDayData.inProgress,
    regularMonthData.inProgress,
    regularWeekData.inProgress,
    searchDayData.inProgress,
    searchMonthData.inProgress,
    searchWeekData.inProgress,
  ]);

  return (
    <Container maxWidth="xll" sx={reportStyles.containerMarginStyles}>
      <Box py={4}>
        {/* Header */}
        <Grid item xs={12} sm={12} md={4} lg={8} xl={10}>
          <Box className="headingTable">
            <h1>Sales & Performance Reports</h1>
          </Box>
        </Grid>

        {/* Filters Section */}
        <Paper elevation={2} sx={reportStyles.paperStyles}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={9}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems="center"
              >
                <Controller
                  name="startDate"
                  control={control}
                  rules={{
                    required: "Start Date is required",
                    validate: (value) => {
                      const end = watch("endDate");
                      if (end && new Date(value) > new Date(end)) {
                        return "Start date must be before or equal to End date";
                      }
                      return true;
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Start Date"
                      type="date"
                      error={!!errors.startDate}
                      helperText={errors.startDate?.message}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                  )}
                />
                <Typography variant="body1" sx={reportStyles.body1Styles}>
                  To
                </Typography>
                <Controller
                  name="endDate"
                  control={control}
                  rules={{
                    required: "End Date is required",
                    validate: (value) => {
                      const start = watch("startDate");
                      if (start && new Date(value) < new Date(start)) {
                        return "End date must be after or equal to Start date";
                      }
                      return true;
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="End Date"
                      type="date"
                      error={!!errors.endDate}
                      helperText={errors.endDate?.message}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                  )}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} md={3}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={handleSubmitDates}
                  fullWidth
                  sx={reportStyles.statckStyles}
                >
                  Submit
                </Button>
                <Tooltip title="Reset Filters">
                  <IconButton onClick={handleReset} color="default">
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {/* Stats and Chart Section */}
        {!isDateSearchActive && showChart && (
          <Paper elevation={3} sx={reportStyles.chartSectionPaperStyles}>
            <Box p={{ xs: 2, md: 3 }}>
              <Grid container spacing={2} alignItems="center" mb={3}>
                <Grid item xs={12} md={8}>
                  <Typography variant="h6" fontWeight={500}>
                    Chart Orders
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={reportStyles.chartSectionBoxStyles}>
                    {Object.values(TIME_FRAMES).map((frame) => (
                      <Button
                        key={frame}
                        onClick={() => handleTimeFrameChange(frame)}
                        sx={{
                          backgroundColor:
                            timeFrame === frame ? "#f5f2f2" : "#ccc",
                          color: "#000",
                          flex: 1,
                          fontSize: { xs: "9px", sm: "12px" },
                          minWidth: { xs: "45px", sm: "70px" },
                          px: { xs: 0.5, sm: 2 },
                        }}
                      >
                        {frame}
                      </Button>
                    ))}
                  </Box>
                </Grid>
              </Grid>

              <Box sx={reportStyles.displayStyles}>
                <Box sx={reportStyles.paddingLeft}>
                  <Box sx={reportStyles.display}>
                    <Avatar
                      variant="square"
                      style={reportStyles.avatarBackGroundColor}
                    >
                      <img
                        src={Bottles}
                        alt="Bottles"
                        style={reportStyles.bottleColor}
                      />
                    </Avatar>
                    <Typography
                      variant="h4"
                      component="h1"
                      fontWeight={600}
                      pt={1}
                    >
                      {salesMetrics.totalSales.toLocaleString()}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    component="p"
                    paddingLeft={2}
                    color="#0056d2"
                  >
                    Total Sales
                  </Typography>
                </Box>
                <Box>
                  <Box sx={reportStyles.display}>
                    <Avatar
                      variant="square"
                      style={reportStyles.avatarBackgroundColor}
                    >
                      <img
                        src={glass}
                        alt="Glass"
                        style={reportStyles.reportImageStyles}
                      />
                    </Avatar>
                    <Typography
                      variant="h4"
                      component="h1"
                      fontWeight={600}
                      pt={1}
                    >
                      {salesMetrics.avgSales.toFixed(2)}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    component="p"
                    paddingLeft={2}
                    color="#0056d2"
                  >
                    Avg Sales per {timeFrame}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Sales Transactions Graph */}
            {chartData.length > 0 && (
              <Box
                sx={{
                  ...reportStyles.perfectScrollBarBoxStyles,
                  height: 350,
                  width: "100%",
                  padding: "0 16px",
                }}
              >
                <PerfectScrollbar>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={chartData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis
                        dataKey="date"
                        tick={{ fill: "#333" }}
                        axisLine={{ stroke: "#e0e0e0" }}
                      />
                      <YAxis
                        tick={{ fill: "#333" }}
                        axisLine={{ stroke: "#e0e0e0" }}
                      />
                      <RechartsTooltip
                        contentStyle={{
                          backgroundColor: "white",
                          border: "1px solid #f0f0f0",
                          borderRadius: "4px",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        }}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="Transactions"
                        stroke="#1b66ef"
                        strokeWidth={3}
                        dot={{ fill: "#1b66ef", r: 6 }}
                        activeDot={{
                          r: 8,
                          stroke: "#1b66ef",
                          strokeWidth: 2,
                          fill: "white",
                        }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </PerfectScrollbar>
              </Box>
            )}
          </Paper>
        )}

        {/* Table Section */}
        <Paper elevation={3} sx={reportStyles.tablePaperStyles}>
          <Box
            p={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" sx={reportStyles.tableTypographyColor}>
              <h2>Wholesaler Sales Data - {timeFrame} View</h2>
            </Typography>

            <Button
              variant="contained"
              startIcon={<FileDownloadIcon />}
              onClick={handleExcelDownload}
              sx={reportStyles.buttonStyles}
              disabled={transformedRows.length === 0}
            >
              Download Excel Report
            </Button>
          </Box>

          <CustomDataGrid
            rows={transformedRows}
            columns={columns}
            pageSize={currentData.pageSize}
            pagination={true}
            loading={isLoading}
            autoHeight
            sx={reportStyles.customGridStyles}
          />

          <Grid container>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <Box px={2}>
                <Pagination
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  totalRecords={
                    timeFrame === TIME_FRAMES.DAY
                      ? totalRecordsDay
                      : timeFrame === TIME_FRAMES.WEEK
                      ? totalRecordsWeek
                      : totalRecordsMonth
                  }
                  pageSize={currentData.pageSize}
                />
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Container>
  );
};

export default WholesalerSalesReportForm;
